import { useState, useCallback, useEffect } from 'react';

export interface UseWizardStateOptions<T> {
  wizardId: string;
  initialData: T;
  totalSteps: number;
  persist?: boolean;
  initialStep?: number;
}

export interface WizardState<T> {
  step: number;
  data: T;
  direction: 'forward' | 'backward';
}

const STORAGE_KEY_PREFIX = 'wizard_state_';

export function useWizardState<T extends object>({
  wizardId,
  initialData,
  totalSteps,
  persist = false,
  initialStep,
}: UseWizardStateOptions<T>) {
  const storageKey = `${STORAGE_KEY_PREFIX}${wizardId}`;

  // Initialize state from localStorage if persisting and data exists
  const getInitialState = (): WizardState<T> => {
    if (persist && typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored) as WizardState<T>;
          return parsed;
        }
      } catch {
        // Ignore parse errors
      }
    }
    return { step: initialStep ?? 0, data: initialData, direction: 'forward' };
  };

  const [state, setState] = useState<WizardState<T>>(getInitialState);

  // Persist state to localStorage
  useEffect(() => {
    if (persist && typeof window !== 'undefined') {
      localStorage.setItem(storageKey, JSON.stringify(state));
    }
  }, [persist, storageKey, state]);

  const setStep = useCallback((step: number, direction: 'forward' | 'backward' = 'forward') => {
    setState((prev) => ({
      ...prev,
      step: Math.max(0, Math.min(totalSteps - 1, step)),
      direction,
    }));
  }, [totalSteps]);

  const nextStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: Math.min(totalSteps - 1, prev.step + 1),
      direction: 'forward',
    }));
  }, [totalSteps]);

  const prevStep = useCallback(() => {
    setState((prev) => ({
      ...prev,
      step: Math.max(0, prev.step - 1),
      direction: 'backward',
    }));
  }, []);

  const updateData = useCallback((partial: Partial<T>) => {
    setState((prev) => ({
      ...prev,
      data: { ...prev.data, ...partial },
    }));
  }, []);

  const reset = useCallback(() => {
    setState({ step: 0, data: initialData, direction: 'forward' });
    if (persist && typeof window !== 'undefined') {
      localStorage.removeItem(storageKey);
    }
  }, [initialData, persist, storageKey]);

  const clearPersisted = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem(storageKey);
    }
  }, [storageKey]);

  const hasPersisted = useCallback(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem(storageKey) !== null;
    }
    return false;
  }, [storageKey]);

  return {
    step: state.step,
    data: state.data,
    direction: state.direction,
    setStep,
    nextStep,
    prevStep,
    updateData,
    reset,
    clearPersisted,
    hasPersisted,
    isFirstStep: state.step === 0,
    isLastStep: state.step === totalSteps - 1,
    totalSteps,
  };
}
